/**
 * Test Evidence dashboard — browse UI-test runs and their screenshots as proof.
 *
 * Runs are stored by the Playwright uploader (e2e/helpers/testEvidence.ts) into the
 * backend's S3-backed store. This screen answers "when was this module last tested,
 * in which environment, against which commit/PR, and what did it look like?".
 *
 * Master/detail: filter + pick a run on the left → its screenshots render on the right
 * (presigned view_urls). view_url can be null until the S3 bucket is configured.
 */
import React, { useState } from 'react';
import { useTestRuns, useTestRun } from '../hooks/useTestEvidence';
import { StatusPill } from '../../../components/manpower/ResourceListing';
import type { RunListItem } from '../../../services/testEvidenceApi';

type Env = 'local' | 'pr' | 'staging';

const ENV_TONE: Record<string, 'gray' | 'blue' | 'green'> = {
	local: 'gray',
	pr: 'blue',
	staging: 'green',
};
const RUN_TONE: Record<string, 'gray' | 'blue' | 'green'> = {
	pending: 'gray',
	passed: 'green',
	failed: 'blue', // (red not in pill set; failed shown via label)
};

export const TestEvidenceDashboard: React.FC = () => {
	const [moduleFilter, setModuleFilter] = useState('');
	const [envFilter, setEnvFilter] = useState<Env | ''>('');
	const [prOnly, setPrOnly] = useState(false);
	const [selectedRun, setSelectedRun] = useState<number | undefined>(undefined);

	const { data, isLoading, error } = useTestRuns({
		module: moduleFilter || undefined,
		environment: envFilter || undefined,
		prOnly: prOnly || undefined,
	});
	const runs: RunListItem[] = data?.runs ?? [];

	const { data: detail, isLoading: detailLoading } = useTestRun(selectedRun);

	const inputCls =
		'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';

	return (
		<div className='min-h-screen bg-white p-6'>
			<div className='max-w-7xl mx-auto'>
				<div className='mb-6'>
					<h1 className='text-xl font-semibold text-gray-900'>Test Evidence</h1>
					<p className='text-sm text-gray-500'>
						UI-test screenshots by module &amp; environment — proof of when each feature was working.
					</p>
				</div>

				{/* Filters */}
				<div className='mb-4 flex flex-wrap items-end gap-3'>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Module</label>
						<input
							type='text'
							value={moduleFilter}
							onChange={e => setModuleFilter(e.target.value)}
							placeholder='e.g. manpower-vendors'
							className={`${inputCls} w-56`}
						/>
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Environment</label>
						<select value={envFilter} onChange={e => setEnvFilter(e.target.value as Env | '')} className={inputCls}>
							<option value=''>All</option>
							<option value='local'>local</option>
							<option value='pr'>pr</option>
							<option value='staging'>staging</option>
						</select>
					</div>
					<label className='flex items-center gap-2 pb-2 text-xs font-medium text-gray-700'>
						<input type='checkbox' checked={prOnly} onChange={e => setPrOnly(e.target.checked)} className='h-4 w-4 accent-green-600' />
						PR runs only
					</label>
				</div>

				<div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
					{/* Runs list */}
					<div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
						<div className='overflow-x-auto'>
							<table className='w-full border-collapse'>
								<thead>
									<tr className='bg-gray-50 border-b border-gray-200'>
										<th className='px-3 py-3 text-left text-xs font-bold text-gray-900'>Module</th>
										<th className='px-3 py-3 text-left text-xs font-bold text-gray-900'>Env</th>
										<th className='px-3 py-3 text-left text-xs font-bold text-gray-900'>When</th>
										<th className='px-3 py-3 text-left text-xs font-bold text-gray-900'>Commit / PR</th>
										<th className='px-3 py-3 text-center text-xs font-bold text-gray-900'>Shots</th>
										<th className='px-3 py-3 text-left text-xs font-bold text-gray-900'>Status</th>
									</tr>
								</thead>
								<tbody>
									{isLoading ? (
										<tr><td colSpan={6} className='px-3 py-8 text-center text-gray-500 text-sm'>Loading runs…</td></tr>
									) : error ? (
										<tr><td colSpan={6} className='px-3 py-8 text-center text-red-600 text-sm'>Failed: {error.message}</td></tr>
									) : runs.length === 0 ? (
										<tr><td colSpan={6} className='px-3 py-8 text-center text-gray-500 text-sm'>No test runs recorded.</td></tr>
									) : (
										runs.map(r => (
											<tr
												key={r.id}
												onClick={() => setSelectedRun(r.id)}
												className={`cursor-pointer border-b border-gray-200 hover:bg-gray-50 ${
													selectedRun === r.id ? 'bg-green-50' : ''
												}`}
											>
												<td className='px-3 py-2 text-xs text-gray-900'>{r.module}</td>
												<td className='px-3 py-2'><StatusPill text={r.environment} tone={ENV_TONE[r.environment ?? 'local']} /></td>
												<td className='px-3 py-2 text-xs text-gray-700'>{r.ran_at}</td>
												<td className='px-3 py-2 text-xs text-gray-700'>
													{r.commit_sha ?? '—'}
													{r.pr_number != null ? ` · PR#${r.pr_number}` : ''}
												</td>
												<td className='px-3 py-2 text-center text-xs text-gray-900'>{r.screenshot_count ?? 0}</td>
												<td className='px-3 py-2'>
													{r.status === 'failed' ? (
														<span className='inline-block rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-medium text-red-700'>failed</span>
													) : (
														<StatusPill text={r.status} tone={RUN_TONE[r.status ?? 'pending']} />
													)}
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
					</div>

					{/* Run detail — screenshots */}
					<div className='bg-white rounded-lg border border-gray-200 p-4'>
						{!selectedRun ? (
							<p className='text-sm text-gray-500'>Select a run to see its screenshots.</p>
						) : detailLoading ? (
							<p className='text-sm text-gray-500'>Loading screenshots…</p>
						) : !detail ? (
							<p className='text-sm text-gray-500'>No detail.</p>
						) : (
							<div>
								<div className='mb-3 text-xs text-gray-600'>
									<span className='font-semibold text-gray-900'>{detail.module}</span> · {detail.environment} ·{' '}
									{detail.ran_at} · {detail.commit_sha ?? '—'}
									{detail.pr_number != null ? ` · PR#${detail.pr_number}` : ''}
								</div>
								<div className='space-y-4'>
									{(detail.screenshots ?? []).map(s => (
										<div key={s.id} className='rounded-md border border-gray-200'>
											<div className='flex items-center justify-between border-b border-gray-100 px-3 py-2 text-xs'>
												<span className='font-medium text-gray-900'>
													{s.test_name ? `${s.test_name} · ` : ''}{s.step_label}
												</span>
												<StatusPill text={s.status} tone={s.status === 'passed' ? 'green' : 'gray'} />
											</div>
											{s.view_url ? (
												<img src={s.view_url} alt={s.step_label ?? ''} className='w-full' loading='lazy' />
											) : (
												<div className='px-3 py-6 text-center text-xs text-gray-400'>
													Image unavailable (S3 bucket not configured yet) — key: {s.s3_key}
												</div>
											)}
										</div>
									))}
								</div>
							</div>
						)}
					</div>
				</div>
			</div>
		</div>
	);
};

export default TestEvidenceDashboard;
